import { z } from "zod";
import { finiteNumber } from "./primitives";

/**
 * Town cars (founder feedback round 3, ADR-0006). Cars are shared, server-simulated street-room
 * entities: everyone sees the same car stop for a pedestrian, and any visitor (guest or resident)
 * can get into a stopped car at its driver's door and drive it on the roads. Cars are never owned,
 * bought, rewarded or persisted; they carry no money, coins, inventory or statistics.
 *
 * Room state (apps/realtime/src/state/schema.ts) adds:
 *   RoomState.vehicles: map<vehicleId, VehicleState> (street rooms only; empty in interiors)
 *   VehicleState { vehicleId: string, style: uint8 (0..VEHICLE_STYLE_COUNT-1), color: uint8 (paint index),
 *                  mode: VehicleMode, x: float64, z: float64, heading: float64 (yaw, 0 = +z),
 *                  speed: float64 (m/s along heading, negative when reversing),
 *                  driverActorId: string ("" unless driven), lastSeq: uint32 (last InputDrive applied) }
 *   ActorState.vehicleId: string ("" while walking; the vehicle the actor is driving otherwise)
 */
export const VEHICLE_STYLE_COUNT = 4;

export const VehicleMode = z.enum([
  /** Following its lane loop; yields to pedestrians and to cars ahead. */
  "traffic",
  /** A visitor is driving it (input.drive). */
  "driven",
  /** Left by its driver; a static obstacle until it returns to traffic. */
  "parked",
]);
export type VehicleMode = z.infer<typeof VehicleMode>;

/**
 * Driving input, sent at the simulation rate while the actor drives (same sequence/budget rules
 * as input.move). throttle +1 = accelerate forward, -1 = brake, then reverse once stopped;
 * steer +1 = turn right relative to the car's heading (clockwise seen from above), -1 = left.
 */
export const InputDrive = z
  .object({
    seq: z.number().int().min(0).max(2_147_483_647),
    throttle: finiteNumber(-1, 1),
    steer: finiteNumber(-1, 1),
  })
  .strict();
export type InputDrive = z.infer<typeof InputDrive>;

/** Get into a stopped car at its driver's door, or get out of the car you are driving. */
export const VehicleAction = z
  .object({
    action: z.enum(["enter", "exit"]),
    /** Required for "enter"; ignored for "exit" (you can only leave your own car). */
    vehicleId: z.string().min(1).max(32).optional(),
  })
  .strict();
export type VehicleAction = z.infer<typeof VehicleAction>;

export const VehicleResultCode = z.enum([
  "ENTERED",
  "EXITED",
  /** Too far from the driver's door (or approaching from the wrong side). */
  "TOO_FAR",
  /** The car is moving; it must be stopped. */
  "MOVING",
  /** Someone else is driving it. */
  "OCCUPIED",
  /** You are already driving another car. */
  "ALREADY_DRIVING",
  /** No free spot next to the car to step out onto. */
  "NO_EXIT_SPACE",
  "NOT_DRIVING",
  "UNKNOWN_VEHICLE",
  "RATE_LIMITED",
  /** Driving is only possible on the street. */
  "NOT_AVAILABLE",
]);
export type VehicleResultCode = z.infer<typeof VehicleResultCode>;

/** Server -> the requesting client only. Other clients see the change through room state. */
export const VehicleResult = z.object({
  action: z.enum(["enter", "exit"]),
  ok: z.boolean(),
  vehicleId: z.string().nullable(),
  code: VehicleResultCode,
  message: z.string(),
});
export type VehicleResult = z.infer<typeof VehicleResult>;
